import { Component, OnDestroy, OnInit } from '@angular/core';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import Highcharts from 'highcharts';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IOriginTons } from 'src/app/modules/widget/interfaces/origin-tons';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { COMPANIES_ID } from 'projects/common/src/app/enumerables/companies-id';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';

@Component({
  selector: 'app-tons-by-origin',
  templateUrl: './tons-by-origin.component.html',
  styleUrls: ['./tons-by-origin.component.scss'],
})
export class TonsByOriginComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public hasData: boolean;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private languageService: LanguageService,
    private formatDatePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  /**
   * Draw chart tons by origin
   */
  public drawView(tonsData?: IOriginTons[]): any {
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const date = this.formatDatePipe.transform(this.parameterService.parameters.item('date').value);
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const { company } = this.cacheService.local('session').user;
    // const isHcm = company.id === COMPANIES_ID.HCM;

    // let subtitleChart = '';

    // if (isHcm) {
    //   subtitleChart = `${this.languageService.message('company')}: ${company.name},
    //             ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
    //             ${this.languageService.message('date')}: ${date},
    //             ${this.languageService.message('shift_')}: ${shift.name}`;
    // } else {
    //   subtitleChart = '';
    // }

    const tonsLang = this.languageService.message('tons');

    this.hasData = !!tonsData;

    const buildedSeries = this.buildSeries(tonsData);

    const data = {
      chart: {
        type: 'column',
        backgroundColor: '#F8F8F8',
        height: 450,
      },
      subtitle: {
        text: `${this.languageService.message('company')}: ${company.name},
                ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                ${this.languageService.message('date')}: ${date},
                ${this.languageService.message('shift_')}: ${shift.name}`,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: [
        {
          labels: {
            style: {
              color: '#707070',
            },
          },
          lineWidth: 1,
          lineColor: '#707070',
          categories: tonsData.map((v) => v.fullZoneName),
          title: {
            text: this.languageService.message('origin').toUpperCase(),
            yAxis: 20,
            style: {
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
        },
      ],
      yAxis: [
        {
          labels: {
            style: {
              color: '#707070',
            },
          },
          lineWidth: 1,
          lineColor: '#707070',
          title: {
            text: this.languageService.message('tons').toUpperCase(),
            yAxis: 20,
            style: {
              fontSize: 12,
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
        },
        {
          title: {
            text: '',
          },
          labels: {
            format: '{value}',
            style: {
              color: '#707070',
            },
          },
          opposite: true,
        },
      ],
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          borderWidth: 2,
          borderColor: '#22D983',
          pointWidth: 30,
          events: {
            legendItemClick: (e) => {
              e.preventDefault();
            },
          },
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            verticalAlign() {
              return this.y === 0 ? 'bottom' : 'top';
            },
            style: {
              color: '#707070',
              textOutline: 'none',
              fontWeight: '600',
              fontSize: '11px',
              fontFamily: 'Open Sans',
            },
            y: -20,
          },
          point: {
            events: {
              click(event: any): void {
                console.log(event);
              },
            },
          },
        },
      },
      tooltip: {
        backgroundColor: '#333333',
        borderColor: '#33333380',
        borderRadius: 2,
        useHTML: true,
        formatter(): string {
          return `
          <div style="width: fit-content; font-size: 13px; color: #FFFFFF;">
            <strong>${this.y} ${tonsLang}</strong>
          </div>`;
        },
      },
      series: buildedSeries,
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              chart: {
                className: 'small-chart',
              },
            },
          },
        ],
      },
      legend: {
        layout: 'horizontal',
        squareSymbol: false,
        align: 'center',
        verticalAlign: 'top',
        margin: 30,
        useHTML: true,
        labelFormatter(): string {
          return `<div style="height: 20px; display: flex;">
                            <svg width="36" height="18" style="float: left;">
                              <rect width="30px" height="12px" x="2" y="2"
                              style="fill: ${this.color}; stroke-width: 2px !important;
                              stroke: #22d983 !important;" />
                            </svg>
                            <span style="font-size: 12px; font-family: Open Sans; font-weight: 400;
                            color: #433E3E; float: left; margin-left: 7px">${this.name}</span>
                          </div>`;
        },
        symbolPadding: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        itemStyle: {
          cursor: 'default',
        },
      },
    };

    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
    window.dispatchEvent(new Event('resize'));
  }

  /**
   *  Build series for tons by origin
   *
   * @param originTons all tons by origin
   */
  private buildSeries(originTons: IOriginTons[]): any {
    const series: any[] = [{ name: this.languageService.message('tons'), id: 'main', data: [], color: 'rgba(34, 217, 131, 0.5)' }];

    let i = 0;
    for (const production of originTons) {
      const data = [];

      for (let c = 0; c < i; c++) {
        data.push(0);
      }

      data.push(production.tonsQty);

      series.push({
        name: production.fullZoneName,
        data,
        linkedTo: 'main',
        color: 'rgba(34, 217, 131, 0.5)',
        states: {
          hover: {
            color: 'rgba(34, 217, 131, 1)',
          },
        },
      });

      i++;
    }
    return series;
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
